import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { API, axios } from '../../utils/api';
import Icona from '../cliente/Icone';

// Ricerca globale della barra dello staff (⌘K / Ctrl+K): clienti, immobili per via, agenzie, documenti,
// raggruppati (backend/ricerca_globale.py). Frecce per scegliere, Invio per aprire, Esc per chiudere.

const GRUPPI = [['clienti', 'Clienti'], ['immobili', 'Immobili'], ['agenzie', 'Agenzie'], ['documenti', 'Documenti']];

// Ogni gruppo è tagliato ai primi sei dal server (`MAX_PER_GRUPPO`, backend/ricerca_globale.py): "via" mostrava sei
// immobili e basta, senza dire che ce n'erano ventitré — chi cercava credeva che fossero tutti e cercava altrove
// quello che non trovava. Come per i suggeriti di "Oggi" (ottavo giro), sotto l'elenco tagliato una riga dice
// quanti ne restano; il totale lo manda il server in un campo a parte ("immobili_totali").
export const TAGLIO_GRUPPO = 6;
const ALTRI = {
  clienti: ["E un altro cliente", (n) => `E altri ${n} clienti`, 'Ce ne sono altri'],
  immobili: ['E un altro immobile', (n) => `E altri ${n} immobili`, 'Ce ne sono altri'],
  agenzie: ["E un'altra agenzia", (n) => `E altre ${n} agenzie`, 'Ce ne sono altre'],
  documenti: ['E un altro documento', (n) => `E altri ${n} documenti`, 'Ce ne sono altri'],
};
// Quanti ne restano fuori: il totale del gruppo meno quelli mostrati. Se il totale non arriva (o non si capisce) la
// riga compare solo con il gruppo pieno e non inventa nessun numero; sotto il taglio non resta niente da dire.
export const altriDelGruppo = (risultati, gruppo) => {
  const mostrati = (risultati?.[gruppo] || []).length;
  const totale = [risultati?.[`${gruppo}_totali`], risultati?.[`${gruppo}_totale`], risultati?.totali?.[gruppo], risultati?.quanti?.[gruppo]]
    .map((x) => Number(x)).find((x) => Number.isFinite(x) && x >= mostrati);
  if (totale === undefined) return mostrati >= TAGLIO_GRUPPO ? { quanti: null } : null;
  return totale > mostrati ? { quanti: totale - mostrati } : null;
};
export const testoAltri = (gruppo, quanti) => {
  const [uno, tanti, senzaNumero] = ALTRI[gruppo] || ALTRI.clienti;
  if (quanti == null) return senzaNumero;
  return quanti === 1 ? uno : tanti(quanti);
};

// Quanto accetta il server (`q: str = Query(..., min_length=2, max_length=80)`, backend/ricerca_globale.py): oltre
// gli 80 caratteri rispondeva 422 e il pannello diceva "Non riesco a cercare. Riprovate", ma riprovare non serviva —
// il campo non deve lasciar scrivere una ricerca che il server rifiuta. Il taglio vale anche per il testo incollato
// da fuori e per quello che parte davvero nella chiamata.
export const MAX_CERCA = 80;

export const useScorciatoiaCerca = (apri) => {
  useEffect(() => {
    const tasto = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        apri();
      }
    };
    document.addEventListener('keydown', tasto);
    return () => document.removeEventListener('keydown', tasto);
  }, [apri]);
};

const RicercaGlobale = ({ aperta, chiudi }) => {
  const [testo, setTesto] = useState('');
  const [risultati, setRisultati] = useState(null);
  // 'ferma' (meno di due lettere) | 'attesa' | 'errore' | 'pronto': una ricerca che si rompe mostrava i risultati
  // di prima o un pannello vuoto, come se non ci fosse niente
  const [stato, setStato] = useState('ferma');
  const [scelto, setScelto] = useState(0);
  const [versione, setVersione] = useState(0);
  const campo = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (aperta) {
      setTesto('');
      setRisultati(null);
      setStato('ferma');
      setScelto(0);
      setTimeout(() => campo.current?.focus(), 0);
    }
  }, [aperta]);

  useEffect(() => {
    if (testo.trim().length < 2) { setRisultati(null); setStato('ferma'); return undefined; }
    let valido = true;
    // mentre cerca non si tengono a video i risultati della parola di prima (sembravano quelli di adesso)
    setRisultati(null);
    setStato('attesa');
    const t = setTimeout(() => {
      axios.get(`${API}/cerca`, { params: { q: testo.trim().slice(0, MAX_CERCA) } })
        .then((r) => { if (valido) { setRisultati(r.data); setScelto(0); setStato('pronto'); } })
        .catch(() => { if (valido) { setRisultati(null); setStato('errore'); } });
    }, 180);
    return () => { valido = false; clearTimeout(t); };
  }, [testo, versione]);

  const piatti = useMemo(() => GRUPPI.flatMap(([g]) => (risultati?.[g] || []).map((r) => ({ ...r, gruppo: g }))), [risultati]);

  if (!aperta) return null;
  const apri = (r) => { chiudi(); navigate(r.link); };
  const tasti = (e) => {
    if (e.key === 'Escape') chiudi();
    else if (e.key === 'ArrowDown') { e.preventDefault(); setScelto((s) => Math.min(s + 1, piatti.length - 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setScelto((s) => Math.max(s - 1, 0)); }
    else if (e.key === 'Enter' && piatti[scelto]) apri(piatti[scelto]);
  };
  let indice = -1;
  return (
    <div className="st-modale" role="dialog" aria-modal="true" aria-label="Cerca" onMouseDown={(e) => { if (e.target === e.currentTarget) chiudi(); }}>
      <div className="scatola">
        <div className="campo">
          <Icona nome="cerca" />
          <input ref={campo} value={testo} maxLength={MAX_CERCA} onChange={(e) => setTesto(e.target.value.slice(0, MAX_CERCA))} onKeyDown={tasti}
            placeholder="Cerca clienti, vie, agenzie, documenti…" aria-label="Cerca" data-testid="ricerca-globale" />
          <kbd>Esc</kbd>
        </div>
        <div className="risultati">
          {stato === 'ferma' && <p className="niente">Scrivete almeno due lettere: un nome, una via, un'agenzia, un documento.</p>}
          {stato === 'attesa' && <p className="niente" role="status" data-testid="cerca-attesa">Cerco…</p>}
          {stato === 'errore' && (
            <p className="niente" role="alert" data-testid="cerca-errore">
              Non riesco a cercare.{' '}
              <button type="button" className="riprova" onClick={() => setVersione((v) => v + 1)}>Riprovate</button>
            </p>
          )}
          {stato === 'pronto' && !piatti.length && <p className="niente">Niente per “{testo}”.</p>}
          {GRUPPI.map(([g, titolo]) => {
            const righe = risultati?.[g] || [];
            if (!righe.length) return null;
            // un elenco tagliato dice quanti ne restano: sei righe sembravano tutto quello che c'era
            const altri = altriDelGruppo(risultati, g);
            return (
              <div key={g}>
                <h4>{titolo}</h4>
                {righe.map((r) => {
                  indice += 1;
                  const mio = indice;
                  return (
                    <button type="button" key={`${g}-${r.id}`} className={`ris${mio === scelto ? ' on' : ''}`} onMouseEnter={() => setScelto(mio)} onClick={() => apri(r)}>
                      {r.foto ? <img className="fotina" src={r.foto} alt="" /> : <span className="fotina">{(r.titolo || '?').charAt(0).toUpperCase()}</span>}
                      <span style={{ minWidth: 0 }}><b>{r.titolo}</b>{r.sottotitolo && <span>{r.sottotitolo}</span>}</span>
                    </button>
                  );
                })}
                {altri && (
                  <p className="altri-ris" data-testid={`altri-${g}`}>
                    {testoAltri(g, altri.quanti)}: scrivete qualche lettera in più.
                  </p>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default RicercaGlobale;
